import type { ComponentChildren, JSX } from 'preact';
import { useId, useRef } from 'preact/hooks';
import { X } from 'lucide-preact';
import { Portal } from './Portal';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock';
import { useHotkey } from '@/hooks/useHotkey';
import { usePresence } from '@/hooks/usePresence';
import { IconButton } from '../primitives/Button';
import { cn } from '@/lib/cn';

export type DialogSize = 'sm' | 'md' | 'lg' | 'xl';

const SIZE: Record<DialogSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-2xl',
};

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title?: ComponentChildren;
  description?: ComponentChildren;
  size?: DialogSize;
  children?: ComponentChildren;
  footer?: ComponentChildren;
  closeOnOverlay?: boolean;
  closeOnEscape?: boolean;
  hideCloseButton?: boolean;
  className?: string;
}

export function Dialog({
  open,
  onClose,
  title,
  description,
  size = 'md',
  children,
  footer,
  closeOnOverlay = true,
  closeOnEscape = true,
  hideCloseButton = false,
  className,
}: DialogProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const descId = useId();
  const { mounted, exiting, onExitEnd } = usePresence(open);

  useFocusTrap(panelRef, open);
  useBodyScrollLock(open);
  useHotkey('Escape', onClose, { enabled: open && closeOnEscape, allowInEditable: true });

  if (!mounted) return null;

  const handleAnimationEnd = (e: JSX.TargetedAnimationEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;
    onExitEnd();
  };

  return (
    <Portal layer="modal">
      <div
        className={cn(
          'fixed inset-0 flex items-center justify-center p-4',
          exiting ? 'animate-fade-out' : 'animate-fade-in',
        )}
        role="presentation"
      >
        <div
          className="absolute inset-0 bg-[var(--scrim)] backdrop-blur-[2px] transition-opacity duration-med"
          onClick={closeOnOverlay && !exiting ? onClose : undefined}
          aria-hidden="true"
          role="presentation"
        />
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={title ? titleId : undefined}
          aria-describedby={description ? descId : undefined}
          onAnimationEnd={handleAnimationEnd}
          className={cn(
            'relative flex w-full flex-col rounded-lg border border-border bg-surface shadow-lg outline-none max-h-[calc(100vh-32px)] dark:border-border/70 dark:shadow-2xl dark:ring-1 dark:ring-white/10',
            exiting ? 'animate-scale-out' : 'animate-slide-up',
            SIZE[size],
            className,
          )}
        >
          {(title || !hideCloseButton) && (
            <header className="flex items-start justify-between gap-3 border-b border-border-subtle px-6 py-4">
              <div className="flex flex-col gap-0.5">
                {title ? (
                  <h2 id={titleId} className="text-base font-semibold tracking-normal text-fg-strong">
                    {title}
                  </h2>
                ) : null}
                {description ? (
                  <p id={descId} className="text-13 text-fg-muted">
                    {description}
                  </p>
                ) : null}
              </div>
              {!hideCloseButton ? (
                <IconButton icon={X} label="Cerrar" size="sm" onClick={onClose} />
              ) : null}
            </header>
          )}
          <div className="flex-1 overflow-auto px-6 py-5">{children}</div>
          {footer ? (
            <footer className="flex items-center justify-end gap-2 border-t border-border-subtle bg-surface-2 px-6 py-4">
              {footer}
            </footer>
          ) : null}
        </div>
      </div>
    </Portal>
  );
}
